import { ApiResponse } from "../types";
import { Constants } from "../utils/constants";

class AddonService {
  private baseURL: string;

  constructor() {
    this.baseURL = Constants.API_BASE_URL;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<ApiResponse> {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth token with fallback like apiV2Service
    let token = localStorage.getItem("authToken");
    if (!token) {
      token = localStorage.getItem("access_token");
    }

    const defaultOptions: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    // Handle 204 No Content and other empty responses
    let data: any = null;
    const contentType = response.headers.get("Content-Type") || "";
    if (response.status !== 204 && contentType.includes("application/json")) {
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }
    }

    return {
      status: response.status,
      data,
      message: data?.message || "",
    };
  }

  // Search addons (public/legacy endpoint)
  async searchAddons(params: {
    duration?: string;
    maxPrice?: number;
    minPrice?: number;
    name?: string;
    page?: number;
    serviceCategory?: string;
    serviceType?: string;
    size?: number;
    sort?: string;
    type?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    const endpoint = `/booking/api/v2/addons/search${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }

  // Get addons for the current user's organization (use this instead of searchAddons)
  // Filters by categoryId (1=Work, 2=Stay, 3=Play) instead of serviceCategory string
  async getMyOrganizationAddons(
    params: {
      categoryId?: number;
      duration?: string;
      maxPrice?: number;
      minPrice?: number;
      name?: string;
      page?: number;
      serviceType?: string;
      size?: number;
      sort?: string;
      type?: string;
    } = {}
  ): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    const endpoint = `/booking/api/v2/addons/my-organization${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    return this.makeRequest(endpoint);
  }

  // Create a new addon
  async createAddon(addon: any): Promise<ApiResponse> {
    return this.makeRequest("/booking/api/v2/addons", {
      method: "POST",
      body: JSON.stringify(addon),
    });
  }

  // Update an existing addon
  async updateAddon(id: number, addon: any): Promise<ApiResponse> {
    return this.makeRequest(`/booking/api/v2/addons/${id}`, {
      method: "PUT",
      body: JSON.stringify(addon),
    });
  }

  // Delete an addon
  async deleteAddon(id: number): Promise<ApiResponse> {
    return this.makeRequest(`/booking/api/v2/addons/${id}`, {
      method: "DELETE",
    });
  }
}

export const addonService = new AddonService();
export default addonService;
